/**
 * Determinism primitives for world gen (ADR-0006): a uint32 string hash,
 * a uint32 mixer, and a small fast PRNG. No external dependencies — keeps
 * runtime determinism stable across Node versions and platforms.
 */

/** FNV-1a 32-bit hash. */
export function hashString(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}

/** Mix two uint32s into one. Avalanches well enough for our needs. */
export function hashCombine(a: number, b: number): number {
  let h = (a ^ Math.imul(b ^ 0x27d4eb2d, 1540483477)) >>> 0;
  h ^= h >>> 15;
  h = Math.imul(h, 0x5bd1e995) >>> 0;
  h ^= h >>> 13;
  return h >>> 0;
}

/** Per-chunk PRNG seed derived from world seed and chunk coord. */
export function hashChunk(seed: number, cq: number, cr: number): number {
  let h = seed >>> 0;
  h = hashCombine(h, cq | 0);
  h = hashCombine(h, cr | 0);
  return h;
}

/** Mulberry32: small, fast, deterministic uniform PRNG in [0, 1). */
export function mulberry32(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
